import { Pto } from '@outcoreaix/pto'
import { RcFile } from 'antd/es/upload'
import { authStorage } from 'app/auth-storage'
import { getQueryParamString } from 'utils/get-query-param-string'
import { apiSlice } from './api-splice'

export const spacesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSpace: builder.query<Pto.Spaces.Space, { spaceId: number } & Pto.Spaces.SingleSpaceQuery>({
      query: ({ spaceId, ...rest }) => {
        const queryParams = getQueryParamString(rest)
        return {
          url: `spaces/${spaceId}?${queryParams}`,
          method: 'GET'
        }
      },
      providesTags: ['SingleSpace']
    }),
    getSpaces: builder.query<Pto.Spaces.List, Pto.Spaces.SpaceListQuery>({
      query: (params) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `spaces?${queryParams}`,
          method: 'GET'
        }
      },
      providesTags: ['Spaces']
    }),
    getSpacesWithMemberCount: builder.query<Pto.Spaces.ListWithMemberCount, Pto.Spaces.SpaceListQuery>({
      query: (params) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `spaces/with-member-count?${queryParams}`,
          method: 'GET'
        }
      },
      providesTags: ['Spaces']
    }),
    getWorkspaceIdBySpace: builder.query<number, number>({
      query: (spaceId) => ({
        url: `spaces/${spaceId}/workspaceId`,
        method: 'GET'
      })
    }),
    getFlowForMember: builder.query<Pto.AppFlows.PopulatedFlow, { flowId: number; spaceId: number }>({
      query: ({ flowId, spaceId }) => ({
        url: `spaces/${spaceId}/app-flows/${flowId}`,
        method: 'GET'
      }),
      providesTags: (_, __, { flowId }) => [{ type: 'Flows', flowId }]
    }),
    getPointsGroupedByCategories: builder.query<Pto.AppFlows.CategoryPoint[], { flowId: number; spaceId: number }>({
      query: ({ flowId, spaceId }) => ({
        url: `spaces/${spaceId}/app-flows/${flowId}/points`,
        method: 'GET'
      }),
      providesTags: (_, __, { flowId }) => [{ type: 'Flows', flowId }]
    }),
    getPointsForProfile: builder.query<Pto.AppFlows.CategoryPoint[], { spaceId: number; memberId: number }>({
      query: ({ spaceId, memberId }) => ({
        url: `spaces/${spaceId}/member-diagram/${memberId}`,
        method: 'GET'
      }),
      providesTags: ['Flows']
    }),
    addSpace: builder.mutation<Pto.Spaces.Space, Pto.Spaces.CreateSpace>({
      query: (data) => ({
        url: 'spaces',
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['Spaces']
    }),
    addSpaceMembers: builder.mutation<void, Pto.Spaces.AddSpaceMembers & { spaceId: number }>({
      query: ({ spaceId, ...rest }) => ({
        url: `spaces/${spaceId}/members`,
        method: 'POST',
        body: rest
      }),
      invalidatesTags: ['Sagas', 'Spaces', 'SpaceMembers', 'Members', 'SingleSpace']
    }),
    activateMember: builder.mutation<void, { memberId: number; spaceId: number }>({
      query: ({ spaceId, memberId }) => ({
        url: `spaces/${spaceId}/activate-member/${memberId}`,
        method: 'PATCH'
      }),
      invalidatesTags: ['SpaceMembers']
    }),
    deactivateMember: builder.mutation<void, { memberId: number; spaceId: number }>({
      query: ({ spaceId, memberId }) => ({
        url: `spaces/${spaceId}/deactivate-member/${memberId}`,
        method: 'PATCH'
      }),
      invalidatesTags: ['SpaceMembers']
    }),
    updateSpace: builder.mutation<void, Pto.Spaces.UpdateSpace & { id: number }>({
      query: ({ id, ...rest }) => ({
        url: `spaces/${id}`,
        method: 'PATCH',
        body: { ...rest }
      }),
      invalidatesTags: ['Spaces', 'SingleSpace']
    }),
    completeProductTour: builder.mutation<void, { spaceId: number }>({
      query: ({ spaceId }) => ({
        url: `spaces/${spaceId}/complete-product-tour`,
        method: 'PATCH'
      }),
      // Optimistically mark the tour complete in the member caches instead of invalidating.
      // A tag invalidation here triggers a member-cache refetch that races the freshly
      // navigated chat's initial send-message request, which leaves the chat with a null
      // starterId and drops it from "Continue where you left off". Patching the cache sets
      // productTourCompletedAt without any refetch, so the tour does not re-show and the
      // concurrent chat send is left undisturbed.
      async onQueryStarted({ spaceId }, { dispatch, queryFulfilled }) {
        const completedAt = new Date().toISOString() as unknown as Date
        const patchCurrent = dispatch(
          spacesApi.util.updateQueryData('getCurrentSpaceMember', { spaceId }, (draft) => {
            if (draft) draft.productTourCompletedAt = completedAt
          })
        )
        const userId = authStorage.user?.id
        const patchMember = userId
          ? dispatch(
              spacesApi.util.updateQueryData('getSpaceMember', { spaceId, id: userId, type: 'users' }, (draft) => {
                if (draft) draft.productTourCompletedAt = completedAt
              })
            )
          : undefined
        try {
          await queryFulfilled
        } catch {
          patchCurrent.undo()
          patchMember?.undo()
        }
      }
    }),
    updateSpaceMemberByUser: builder.mutation<void, Partial<Pto.Spaces.UpdateSpaceMember> & { spaceId: number }>({
      query: ({ spaceId, ...rest }) => ({
        url: `spaces/${spaceId}/update-space-member-by-user`,
        method: 'PATCH',
        body: { ...rest, userId: authStorage.user!.id }
      }),
      invalidatesTags: ['SpaceMembers', 'CurrentSpaceMember']
    }),
    saveCareerStorySnapshot: builder.mutation<
      void,
      { spaceId: number; careerStorySnapshot: string; firstName?: string }
    >({
      query: ({ spaceId, careerStorySnapshot, firstName }) => ({
        url: `spaces/${spaceId}/career-story-snapshot`,
        method: 'PATCH',
        body: { careerStorySnapshot, firstName }
      }),
      invalidatesTags: ['SpaceMembers', 'CurrentSpaceMember']
    }),
    saveSkillsDiagram: builder.mutation<void, { spaceId: number; skillsDiagramData: Pto.Artifacts.SkillsDiagramData }>({
      query: ({ spaceId, skillsDiagramData }) => ({
        url: `spaces/${spaceId}/skills-diagram`,
        method: 'PATCH',
        body: { skillsDiagramData }
      }),
      invalidatesTags: ['SpaceMembers', 'CurrentSpaceMember']
    }),
    saveTopJobs: builder.mutation<void, { spaceId: number; topJobs: Pto.Artifacts.TopJob[] }>({
      query: ({ spaceId, topJobs }) => ({
        url: `spaces/${spaceId}/top-jobs`,
        method: 'PATCH',
        body: { topJobs }
      }),
      invalidatesTags: ['SpaceMembers', 'CurrentSpaceMember']
    }),
    saveExploredJobs: builder.mutation<void, { spaceId: number; exploredJobs: Pto.Artifacts.ExploredJob[] }>({
      query: ({ spaceId, exploredJobs }) => ({
        url: `spaces/${spaceId}/explored-jobs`,
        method: 'PATCH',
        body: { exploredJobs }
      }),
      invalidatesTags: ['SpaceMembers', 'CurrentSpaceMember']
    }),
    saveCareerFit: builder.mutation<void, { spaceId: number; careerFit: Pto.Artifacts.CareerFit }>({
      query: ({ spaceId, careerFit }) => ({
        url: `spaces/${spaceId}/career-fit`,
        method: 'PATCH',
        body: { careerFit }
      }),
      invalidatesTags: ['SpaceMembers', 'CurrentSpaceMember']
    }),
    updateSpaceMemberRole: builder.mutation<
      void,
      Pto.Spaces.UpdateSpaceMemberRole & { spaceId: number; memberId: number }
    >({
      query: ({ spaceId, memberId, ...rest }) => ({
        url: `spaces/${spaceId}/change-member-role/${memberId}`,
        method: 'PATCH',
        body: { ...rest }
      }),
      invalidatesTags: ['SpaceMembers']
    }),
    updateSpaceMemberName: builder.mutation<void, Pto.Users.UpdateUser & { spaceId: number; memberId: number }>({
      query: ({ spaceId, memberId, ...rest }) => ({
        url: `spaces/${spaceId}/members/${memberId}/name`,
        method: 'PATCH',
        body: { ...rest }
      }),
      invalidatesTags: ['SpaceMembers', 'Members']
    }),
    updateSpaceMembers: builder.mutation<void, { file: RcFile; id: number }>({
      query: ({ id, file }) => {
        const body = new FormData()
        body.append('Content-Type', file.type)
        body.append('file', file)
        return {
          url: `spaces/update-space-contacts/${id}`,
          method: 'POST',
          body
        }
      },
      invalidatesTags: ['Spaces', 'SpaceMembers', 'Members', 'SingleSpace']
    }),
    getSpaceMembers: builder.query<Pto.Spaces.MemberWithInviteList, Pto.Spaces.MemberListQuery>({
      query: (params) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `spaces/members/list?${queryParams}`,
          method: 'GET'
        }
      },
      providesTags: ['SpaceMembers']
    }),
    getSpaceMembersByRole: builder.query<Pto.Spaces.SpaceMemberForEmailItem[], Pto.Spaces.SpaceMembersByRoleQuery>({
      query: (params) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `spaces/members/by-role?${queryParams}`,
          method: 'GET'
        }
      },
      providesTags: ['SpaceMembers']
    }),
    getSpaceMembersCSVLink: builder.query<Pto.Spaces.SpaceMembersCsv, Pto.Spaces.MemberListQuerySV>({
      query: (params) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `spaces/members/csv/?${queryParams}`,
          method: 'GET'
        }
      },
      providesTags: ['SpaceMembers']
    }),
    getStaffEngagementCSV: builder.query<Pto.Spaces.SpaceMembersCsv, Pto.Spaces.MemberListQuery>({
      query: (params) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `spaces/staff/csv/?${queryParams}`,
          method: 'GET'
        }
      },
      providesTags: ['SpaceMembers']
    }),
    getCurrentSpaceMember: builder.query<Pto.Spaces.SpaceMember, { spaceId: number }>({
      query: ({ spaceId }) => ({
        url: `spaces/${spaceId}/membership/users/${authStorage.user?.id}`,
        method: 'GET'
      }),
      providesTags: ['CurrentSpaceMember']
    }),
    getSpaceMember: builder.query<Pto.Spaces.SpaceMember, { spaceId: number; id: number; type: 'users' | 'members' }>({
      query: ({ spaceId, id, type }) => ({
        url: `spaces/${spaceId}/membership/${type}/${id}`,
        method: 'GET'
      }),
      providesTags: (result, error, { id, type }) =>
        type === 'users' ? [{ type: 'SpaceMembers', userId: id }] : [{ type: 'SpaceMembers', id }]
    }),
    getSpaceMembersForWorkspaceMember: builder.query<
      Pto.Spaces.SpaceMemberShort[],
      { workspaceMemberId: number; spaceId: number }
    >({
      query: ({ workspaceMemberId, spaceId }) => ({
        url: `spaces/${spaceId}/spaceMembershipForWorkspaceMember/${workspaceMemberId}`,
        method: 'GET'
      }),
      providesTags: (_, __, { workspaceMemberId }) => [{ type: 'Members', id: workspaceMemberId }]
    }),
    deleteSpaceMember: builder.mutation<void, { spaceId: number; memberId: number }>({
      query: ({ spaceId, memberId }) => ({
        url: `spaces/${spaceId}/members/${memberId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['SpaceMembers']
    }),
    getMembersDashboard: builder.query<Pto.Spaces.Dashboard, Pto.Spaces.DashboardQuery>({
      query: (params) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `spaces/members/dashboard/?${queryParams}`,
          method: 'GET'
        }
      }
    }),
    getPotentialMembersForOptions: builder.query<
      Pto.Meta[],
      { spaceId: number } & Pto.Workspaces.GetWorkspaceMembersForOptionsQuery
    >({
      query: ({ spaceId, workspaceId, searchText, excludeRoles }) => {
        const queryParams = getQueryParamString({ workspaceId, searchText, excludeRoles })
        return {
          url: `spaces/${spaceId}/potential-members/?${queryParams}`,
          method: 'GET'
        }
      },
      providesTags: ['SpaceMembers']
    }),
    getMemberAnswers: builder.query<Pto.Spaces.MemberAnswer[], number>({
      query: (spaceId) => ({
        url: `spaces/${spaceId}/member-answers`,
        method: 'GET'
      }),
      providesTags: ['MemberAnswers']
    }),
    getMemberSystemAnswers: builder.query<Pto.Spaces.MemberAnswer[], number>({
      query: (spaceId) => ({
        url: `spaces/${spaceId}/member-system-answers`,
        method: 'GET'
      }),
      providesTags: ['MemberSystemAnswers']
    }),
    getMemberAnswersByMemberId: builder.query<
      (Pto.Spaces.MemberAnswer | Pto.Spaces.InductionAnswer)[],
      { spaceId: number; memberId: number }
    >({
      query: ({ spaceId, memberId }) => ({
        url: `spaces/${spaceId}/member/${memberId}/all-member-answers`,
        method: 'GET'
      }),
      providesTags: ['MemberAnswers']
    }),
    updateSpaceKnowledgeBase: builder.mutation<void, { file: RcFile; id: number }>({
      query: ({ id, file }) => {
        const body = new FormData()
        body.append('Content-Type', file.type)
        body.append('file', file)

        return {
          url: `spaces/${id}/knowledge-base`,
          method: 'PATCH',
          body
        }
      },
      invalidatesTags: ['Spaces', 'SingleSpace']
    }),
    updateSpaceAIConfig: builder.mutation<void, Pto.Spaces.UpdateSpaceAIConfig & { id: number }>({
      query: ({ id, ...data }) => {
        return {
          url: `spaces/${id}/ai-config`,
          method: 'PATCH',
          body: data,
          prepareHeaders: (headers: any) => {
            headers.set('Content-Type', 'text/plain')
            return headers
          }
        }
      },
      invalidatesTags: ['Spaces', 'SingleSpace']
    }),
    updateSpaceAIAvatar: builder.mutation<void, { id: number; file: RcFile }>({
      query: ({ id, file }) => {
        const body = new FormData()
        body.append('Content-Type', file.type)
        body.append('file', file)

        return {
          url: `spaces/${id}/ai-avatar`,
          method: 'PATCH',
          body
        }
      },
      invalidatesTags: ['Spaces', 'SingleSpace']
    }),
    addSpaceQuestions: builder.mutation<void, { createQuestionsDto: Pto.Spaces.CreateSpaceQuestion[]; id: number }>({
      query: ({ id, createQuestionsDto }) => ({
        url: `spaces/${id}/questions`,
        method: 'POST',
        body: createQuestionsDto
      }),
      invalidatesTags: ['Spaces', 'SingleSpace']
    }),
    addQuestionAnswer: builder.mutation<any, { id: number } & Pto.Spaces.UpsertSpaceMemberAnswer>({
      query: ({ id, ...rest }) => ({
        url: `spaces/${id}/answer`,
        method: 'PATCH',
        body: rest
      }),
      invalidatesTags: ['MemberAnswers']
    }),
    deleteSpace: builder.mutation<void, number>({
      query: (id) => ({
        url: `spaces/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Spaces', 'SingleSpace']
    }),
    generateSummary: builder.mutation<Pto.Spaces.SpaceMember, number>({
      query: (id) => ({
        url: `spaces/${id}/generate-summary`,
        method: 'PATCH'
      }),
      invalidatesTags: [
        'Spaces',
        'MemberStatus',
        'SingleSpace',
        'CurrentSpaceMember',
        'MemberSystemAnswers',
        'SpaceMembers'
      ]
    }),
    enhancePrompt: builder.mutation<Pto.Spaces.EnhancedPrompt, Pto.Spaces.EnhancePrompt & { spaceId: number }>({
      query: ({ spaceId, ...rest }) => ({
        url: `spaces/${spaceId}/enhance-prompt`,
        method: 'POST',
        body: rest
      })
    }),
    generateFlowSummary: builder.mutation<string, { flowId: number; spaceId: number }>({
      query: ({ flowId, spaceId }) => ({
        url: `spaces/${spaceId}/generate-flow-summary/${flowId}`,
        method: 'PATCH',
        responseHandler: 'text'
      }),
      invalidatesTags: ['Starters']
    }),
    getPersonalisedStarters: builder.query<Pto.AppTemplates.StarterList, number>({
      query: (id) => ({
        url: `spaces/${id}/personalised-starters`,
        method: 'GET'
      }),
      providesTags: ['PersonalizedStarters']
    }),
    getCompletedStarterIds: builder.query<number[], number>({
      query: (spaceId) => ({
        url: `spaces/${spaceId}/completed-starters`,
        method: 'GET'
      }),
      providesTags: ['Starters']
    }),
    getContinueWhereLeftOffStarters: builder.query<Pto.Analytics.ContinueWhereLeftOffStarter[], number>({
      query: (spaceId) => ({
        url: `spaces/${spaceId}/continue-where-left-off-starters`,
        method: 'GET'
      }),
      providesTags: ['Chats']
    }),
    getMemberSpaces: builder.query<Pto.Spaces.Space[], { workspaceId: number; userId: number }>({
      query: ({ workspaceId, userId }) => ({
        url: `spaces/workspace/${workspaceId}/user/${userId}`,
        method: 'GET'
      }),
      providesTags: ['Spaces']
    }),
    updateSummary: builder.mutation<void, Pto.Spaces.UpdateSpaceMemberSummary & { id: number }>({
      query: ({ id, ...rest }) => ({
        url: `spaces/${id}/update-summary`,
        method: 'PATCH',
        body: rest
      }),
      invalidatesTags: ['Spaces', 'MemberStatus']
    }),
    checkMembership: builder.query<
      Pto.Spaces.SpaceWithAnswers | undefined,
      Partial<Pto.Spaces.CheckUncompletedMembershipQuery>
    >({
      query: (params) => {
        const queryString = getQueryParamString(params)

        return {
          url: `/spaces/check/uncompleted-membership?${queryString}`,
          method: 'GET'
        }
      },
      providesTags: ['MemberStatus', 'MemberAnswers']
    }),
    createSpaceToken: builder.mutation<
      Pto.Spaces.SpaceInvitationToken,
      Pto.Spaces.CreateSpaceInvitationToken & { spaceId: number }
    >({
      query: ({ spaceId, ...body }) => ({
        url: `spaces/${spaceId}/invitation-token`,
        method: 'POST',
        body
      })
    }),
    updateSpaceToken: builder.mutation<
      Pto.Spaces.SpaceInvitationToken,
      Pto.Spaces.UpdateSpaceInvitationToken & { spaceId: number }
    >({
      query: ({ spaceId, ...body }) => ({
        url: `spaces/${spaceId}/invitation-token`,
        method: 'PATCH',
        body
      })
    }),
    convertAudioToText: builder.mutation<{ text: string }, { file: Blob; id: number }>({
      query: ({ id, file }) => {
        const body = new FormData()
        body.append('Content-Type', file.type)
        body.append('file', file)

        return {
          url: `spaces/${id}/audio-to-text`,
          method: 'POST',
          body
        }
      }
    }),
    getSpaceToken: builder.query<Pto.Spaces.SpaceInvitationToken | undefined, number>({
      query: (spaceId) => ({
        url: `spaces/${spaceId}/space-token`,
        method: 'GET'
      })
    }),
    getSpacePluginToken: builder.query<Pto.Spaces.SpaceInvitationToken | undefined, number>({
      query: (spaceId) => ({
        url: `spaces/${spaceId}/space-plugin-token`,
        method: 'GET'
      }),
      providesTags: ['SpacePluginToken']
    }),
    createSpacePluginToken: builder.mutation<
      Pto.Spaces.SpaceInvitationToken,
      Pto.Spaces.CreateSpacePluginInvitationToken & { spaceId: number }
    >({
      query: ({ spaceId, ...body }) => ({
        url: `spaces/${spaceId}/plugin-invitation-token`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['SpacePluginToken']
    }),
    createSpaceAIPlugin: builder.mutation<
      Pto.Spaces.SpaceAIPlugin,
      Pto.Spaces.CreateSpaceAIPlugin & { spaceId: number }
    >({
      query: ({ spaceId, ...body }) => ({
        url: `spaces/${spaceId}/ai-plugin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['SpaceAIPlugin']
    }),
    getSpaceAIPlugin: builder.query<Pto.Spaces.SpaceAIPlugin | undefined, number>({
      query: (spaceId) => ({
        url: `spaces/${spaceId}/ai-plugin`,
        method: 'GET'
      }),
      providesTags: ['SpaceAIPlugin']
    }),
    getAllMembersWithoutInduction: builder.query<
      Pto.Spaces.MemberWithoutInductionList,
      { page?: number; size?: number }
    >({
      query: (params) => ({
        url: `spaces/members-without-induction`,
        method: 'GET',
        params
      }),
      providesTags: ['SpaceMembers']
    }),
    getFlowTriggeredStarters: builder.query<Pto.AppTemplates.Starter[], number>({
      query: (spaceId) => ({
        url: `spaces/${spaceId}/flow-triggered-starters`,
        method: 'GET'
      }),
      providesTags: (_, __, spaceId) => [{ type: 'Starters', id: spaceId }]
    }),
    completeFlowTriggeredStarter: builder.mutation<{ completed: boolean }, { flowId: number; spaceId: number }>({
      query: ({ flowId, spaceId }) => ({
        url: `spaces/${spaceId}/complete-flow-triggered-starter/${flowId}`,
        method: 'PATCH'
      }),
      invalidatesTags: (_, __, { spaceId }) => [{ type: 'Starters', id: spaceId }, 'PersonalizedStarters', 'Starters']
    }),
    getCompletedFlowTriggeredStarters: builder.query<Pto.AppTemplates.Starter[], number>({
      query: (spaceId) => ({
        url: `spaces/${spaceId}/completed-flow-triggered-starters`,
        method: 'GET'
      }),
      providesTags: (_, __, spaceId) => [{ type: 'Starters', id: spaceId }]
    }),
    getSpaceMemberInterests: builder.query<string[], number>({
      query: (spaceId) => ({
        url: `spaces/${spaceId}/interests`,
        method: 'GET'
      }),
      providesTags: (_, __, spaceId) => [{ type: 'SpaceMemberInterests', id: spaceId }]
    }),
    addInterest: builder.mutation<void, { spaceId: number; activity: string }>({
      query: ({ spaceId, activity }) => ({
        url: `spaces/${spaceId}/interests`,
        method: 'POST',
        body: { activity }
      }),
      async onQueryStarted({ spaceId, activity }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          spacesApi.util.updateQueryData('getSpaceMemberInterests', spaceId, (draft) => {
            if (!Array.isArray(draft)) return
            if (!draft.includes(activity)) draft.push(activity)
          })
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      }
    }),
    removeInterest: builder.mutation<void, { spaceId: number; activity: string }>({
      query: ({ spaceId, activity }) => ({
        url: `spaces/${spaceId}/interests`,
        method: 'DELETE',
        body: { activity }
      }),
      async onQueryStarted({ spaceId, activity }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          spacesApi.util.updateQueryData('getSpaceMemberInterests', spaceId, (draft) => {
            if (!Array.isArray(draft)) return
            const idx = draft.indexOf(activity)
            if (idx !== -1) draft.splice(idx, 1)
          })
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      }
    })
  })
})

export const {
  useGetSpaceQuery,
  useAddSpaceMutation,
  useActivateMemberMutation,
  useDeactivateMemberMutation,
  useAddSpaceMembersMutation,
  useUpdateSpaceKnowledgeBaseMutation,
  useUpdateSpaceAIConfigMutation,
  useAddSpaceQuestionsMutation,
  useUpdateSpaceMutation,
  useUpdateSpaceMembersMutation,
  useGetSpacesQuery,
  useGetSpacesWithMemberCountQuery,
  useGetFlowForMemberQuery,
  useGetPointsGroupedByCategoriesQuery,
  useGetPointsForProfileQuery,
  useGetSpaceMembersQuery,
  useGetCurrentSpaceMemberQuery,
  useGetSpaceMembersCSVLinkQuery,
  useGetStaffEngagementCSVQuery,
  useLazyGetStaffEngagementCSVQuery,
  useGetSpaceMemberQuery,
  useGetSpaceMembersByRoleQuery,
  useGetSpaceMembersForWorkspaceMemberQuery,
  useGetWorkspaceIdBySpaceQuery,
  useGetMembersDashboardQuery,
  useGetPotentialMembersForOptionsQuery,
  useGetPersonalisedStartersQuery,
  useGetCompletedStarterIdsQuery,
  useGetContinueWhereLeftOffStartersQuery,
  useDeleteSpaceMutation,
  useDeleteSpaceMemberMutation,
  useAddQuestionAnswerMutation,
  useGenerateSummaryMutation,
  useEnhancePromptMutation,
  useGenerateFlowSummaryMutation,
  useCompleteFlowTriggeredStarterMutation,
  useGetMemberSpacesQuery,
  useUpdateSummaryMutation,
  useCheckMembershipQuery,
  useUpdateSpaceMemberRoleMutation,
  useUpdateSpaceMemberNameMutation,
  useUpdateSpaceMemberByUserMutation,
  useCompleteProductTourMutation,
  useSaveCareerStorySnapshotMutation,
  useSaveSkillsDiagramMutation,
  useSaveTopJobsMutation,
  useSaveExploredJobsMutation,
  useSaveCareerFitMutation,
  useGetSpaceTokenQuery,
  useLazyGetSpaceMembersQuery,
  useCreateSpaceTokenMutation,
  useUpdateSpaceTokenMutation,
  useUpdateSpaceAIAvatarMutation,
  useGetMemberAnswersQuery,
  useGetMemberSystemAnswersQuery,
  useCreateSpaceAIPluginMutation,
  useCreateSpacePluginTokenMutation,
  useGetSpacePluginTokenQuery,
  useGetSpaceAIPluginQuery,
  useGetMemberAnswersByMemberIdQuery,
  useConvertAudioToTextMutation,
  useGetAllMembersWithoutInductionQuery,
  useGetFlowTriggeredStartersQuery,
  useGetCompletedFlowTriggeredStartersQuery,
  useGetSpaceMemberInterestsQuery,
  useAddInterestMutation,
  useRemoveInterestMutation
} = spacesApi
